import { Entity, Nullable } from '@inter-ikea-core/core';
import {
  PlannerProductComponent,
  Product,
  SelectionOverrideComponent
} from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { getMarketProducts, getSelectedProduct } from 'ducks/products';
import * as R from 'ramda';
import {
  ProductGroup,
  SelectedMenu,
  SelectedMenuFilter,
  SelectedMenuProductList
} from 'rangeConfig/@types';
import isWidget from 'rangeConfig/shared/utils/isWidget';
import React, { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { WidgetContextValue } from './widget';
import { useBehaviorTracker } from './behaviors';

type SelectedProviderProps = {
  visible: boolean;
  selectedMenu?: SelectedMenu;
  selectedGroup?: ProductGroup;
  additionalContent?: SelectedMenuProductList;
  toggleAdditionalContent: (
    content: SelectedMenuProductList | undefined,
    widgetCtx?: WidgetContextValue
  ) => void;
  unselect: () => void;
  hiddenContent: (content: SelectedMenuProductList) => boolean;
  noThumbs: (content: SelectedMenuProductList) => boolean;
  getContentProducts: (content: SelectedMenuProductList) => Product[];
  getSelectedIds: (content: SelectedMenuProductList) => string[];
  widgetContext?: WidgetContextValue;
};

export const SelectedContext = React.createContext<SelectedProviderProps>({
  visible: false,
  selectedMenu: undefined,
  additionalContent: undefined,
  toggleAdditionalContent: () => {},
  unselect: () => {},
  hiddenContent: () => false,
  noThumbs: () => false,
  getContentProducts: () => [],
  getSelectedIds: () => []
});

export const useSelected = () => useContext(SelectedContext);

type Props = {
  children: React.ReactNode;
};

const getSelectedGroup = (
  productGroups: ProductGroup[],
  selectedProduct: Product
): ProductGroup | undefined => {
  const selectedGroup = productGroups.find(group =>
    group.productTypes.includes(selectedProduct.type)
  );
  return selectedGroup;
};

export const SelectedProvider: FunctionComponent<Props> = ({ children }) => {
  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();
  const coreApi = useCore();
  const products = useSelector(getMarketProducts);
  const selectedProduct = useSelector(getSelectedProduct);
  const selectedEntity = selectedProduct ? coreApi.entities.find(selectedProduct.entityId) : null;
  const behaviorTracker = useBehaviorTracker();

  const selectedGroup = selectedProduct
    ? getSelectedGroup(productGroups, selectedProduct)
    : undefined;

  const selectedMenu = selectedGroup?.selectedMenu;

  const [lastSelected, setLastSelected] = useState({ selectedProduct, selectedGroup });

  useEffect(() => {
    if (selectedProduct) setLastSelected({ selectedProduct, selectedGroup });
  }, [selectedProduct, selectedGroup]);

  const [additionalContent, setAdditionalContent] = useState<SelectedMenuProductList | undefined>(
    undefined
  );

  const [widgetContext, setWidgetContext] = useState<WidgetContextValue | undefined>(undefined);

  const hiddenContent = (content: SelectedMenuProductList) => {
    if (content.hidden && selectedProduct) {
      return content.hidden(coreApi, selectedProduct.entityId);
    }
    return false;
  };

  const checkForMenuContent = (selectedMenu?: SelectedMenu): boolean => {
    if (!selectedMenu || selectedMenu.content.length === 0) {
      return false;
    }

    return (
      selectedMenu.content.filter(content => !isWidget(content)).length !==
      selectedMenu.content.filter(
        content => !isWidget(content) && hiddenContent(content as SelectedMenuProductList)
      ).length
    );
  };

  const selectedMenuVisible =
    !!selectedEntity &&
    !selectedEntity?.hasComponent(SelectionOverrideComponent.id) &&
    checkForMenuContent(selectedMenu);

  const unselect = () => {
    coreApi.entities.unselect();
  };

  const toggleAdditionalContent = (
    content: SelectedMenuProductList | undefined,
    widgetCtx?: WidgetContextValue
  ) => {
    const newContext = widgetCtx;
    const oldContext = widgetContext;

    if (!R.equals(content, additionalContent) && oldContext) {
      behaviorTracker.withContext(oldContext).trigger('listCollapsed', null);
    }

    if (content && newContext) {
      behaviorTracker.withContext(newContext).trigger('listExpanded', null);
    }

    setAdditionalContent(content);
    setWidgetContext(newContext);
  };

  const noThumbs = (content: SelectedMenuProductList) => {
    if (content.noThumbs && selectedProduct) {
      return content.noThumbs(coreApi, selectedProduct.entityId);
    }
    return false;
  };

  const getContentProducts = useCallback(
    (content: SelectedMenuProductList): Product[] => {
      let contentProducts: Product[] = [];

      const filterProducts = (
        selectedProduct: Product,
        selectedEntity: Nullable<Entity>,
        products: Product[]
      ) =>
        R.curry(
          R.reduce((acc: Product[], filter: SelectedMenuFilter) => {
            return R.filter(filter(coreApi, selectedProduct, selectedEntity || undefined))(acc);
          }, products)
        );

      if (selectedProduct) {
        const { productTypes, productFilters } = content as SelectedMenuProductList;

        if (productTypes && productTypes.length > 0) {
          contentProducts = products.filter((product: Product) =>
            productTypes.some((type: string) => type === product.type)
          );
        } else {
          contentProducts = products.filter(
            (product: Product) => product.type === selectedProduct.type
          );
        }

        return filterProducts(selectedProduct, selectedEntity, contentProducts)(productFilters);
      }

      return contentProducts;
    },
    [coreApi, products, selectedEntity, selectedProduct]
  );

  const getSelectedIds = (content: SelectedMenuProductList): string[] => {
    if (!selectedProduct || !selectedEntity) {
      return [];
    }
    const getChildrenIds = (): string[] => {
      const selectedEntity = coreApi.entities.findSelectedEntity();
      if (!selectedEntity) {
        return [];
      }
      const childrenOfEntity = coreApi.entities.getDescendants(selectedEntity);
      return childrenOfEntity.map(({ productId }) => productId);
    };

    const sceneIds = selectedEntity.hasComponent(PlannerProductComponent.id)
      ? [selectedProduct.coreProductId, ...getChildrenIds()]
      : getChildrenIds();

    const contentProducts = getContentProducts(content);

    let selected = contentProducts
      .filter(({ coreProductId }) => sceneIds.includes(coreProductId))
      .map(p => p.coreProductId);

    if (content.overrideSelection) {
      selected = content.overrideSelection(selectedEntity, coreApi);
    }

    return selected;
  };

  useEffect(() => {
    if (!selectedProduct) {
      setAdditionalContent(undefined);
      behaviorTracker
        .withContext({ menuType: 'selected', groupId: lastSelected.selectedGroup?.id ?? '' })
        .trigger('selectedMenuExited', null);
    } else {
      behaviorTracker
        .withContext({ menuType: 'selected', groupId: selectedGroup?.id ?? '' })
        .trigger('selectedMenuEntered', null);
    }
    if (additionalContent) {
      const content = selectedMenu?.content.find(c => c.id === additionalContent.id);
      if (!content) {
        setAdditionalContent(undefined);
      }
      if (content && hiddenContent(content as SelectedMenuProductList)) {
        setAdditionalContent(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  return (
    <SelectedContext.Provider
      value={{
        visible: selectedMenuVisible,
        selectedGroup,
        selectedMenu,
        additionalContent,
        toggleAdditionalContent,
        unselect,
        hiddenContent,
        noThumbs,
        getContentProducts,
        getSelectedIds,
        widgetContext
      }}
    >
      {children}
    </SelectedContext.Provider>
  );
};
